"use strict";
/**
 * SURCHARGE RATES — the ONE rule for "what rate and flat does this location
 * actually charge", read the same way by the payment service, every admin save
 * and the till.
 *
 * `tenantSurchargeConfig.rateOverride` / `flatOverride` are the merchant's own
 * numbers inside the super-admin ceilings (`maxSurchargeRate` /
 * `maxSurchargeFlat`, seeded from the active agreement).
 *
 * TWO MEANINGS, chosen by `overridesExplicit`:
 *
 *   LEGACY (overridesExplicit absent/false) — every row written before
 *   2026-10-07. 0, null or absent = "use the max"; a positive number is used,
 *   clamped to the max. A merchant could never choose "0%".
 *
 *   EXPLICIT (overridesExplicit === true) — a finite number, INCLUDING 0, is
 *   literal (clamped to [0, max]); null / absent = "use the max".
 *
 * A legacy row is converted (toExplicitSurchargeOverrides) the first time a
 * save touches its rate or flat, so it never silently flips meaning: legacy 0
 * becomes null, which is "use the max" under both readings.
 *
 * Safety: the result is never negative, never above the max, and a missing /
 * NaN / infinite max is 0 — garbage never produces a surcharge.
 *
 * Mirrored (identical logic, no shared-types dependency) in
 * pos-payments-service and super-admin-ext2 `src/lib/surcharge-rates.ts`.
 * Change all three together.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.effectiveSurchargeRates = effectiveSurchargeRates;
exports.toExplicitSurchargeOverrides = toExplicitSurchargeOverrides;
exports.mergeSurchargeOverrides = mergeSurchargeOverrides;
function ceiling(max) {
    const n = typeof max === 'string' && max.trim() !== '' ? Number(max) : max;
    return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0;
}
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
function pick(value, explicit, max) {
    if (!isNum(value))
        return max; // null / absent / NaN / non-number = use max
    if (!explicit && value <= 0)
        return max; // legacy: 0 (or a stray negative) = use max
    return Math.min(Math.max(value, 0), max); // literal, clamped to [0, max]
}
/** The rate (percent) and flat a location actually charges. */
function effectiveSurchargeRates(config, maxRate, maxFlat) {
    const r = ceiling(maxRate);
    const f = ceiling(maxFlat);
    const explicit = config?.overridesExplicit === true;
    return {
        rate: pick(config?.rateOverride, explicit, r),
        flat: pick(config?.flatOverride, explicit, f),
    };
}
function explicitValue(value, explicit) {
    if (!isNum(value) || value < 0)
        return null;
    if (!explicit && value === 0)
        return null;
    return value;
}
/**
 * The same stored config under EXPLICIT meaning, with identical effective
 * rates. Other keys (`apply`, anything future) are kept.
 */
function toExplicitSurchargeOverrides(config) {
    const explicit = config?.overridesExplicit === true;
    return {
        ...(config || {}),
        rateOverride: explicitValue(config?.rateOverride, explicit),
        flatOverride: explicitValue(config?.flatOverride, explicit),
        overridesExplicit: true,
    };
}
/**
 * A save's next `tenantSurchargeConfig`: the stored row converted to explicit
 * first, then the body applied (absent = keep; null = use max; number = that
 * number, clamped down to a NON-ZERO ceiling — a 0 ceiling means "no agreement
 * yet" and keeps the merchant's number, which reading clamps to 0 anyway).
 * An empty body just re-clamps the stored values to the ceilings.
 */
function mergeSurchargeOverrides(stored, input, ceilings) {
    const base = toExplicitSurchargeOverrides(stored);
    const body = input || {};
    const bodyExplicit = body.overridesExplicit === true;
    const maxRate = ceiling(ceilings.maxRate);
    const maxFlat = ceiling(ceilings.maxFlat);
    const next = (key, max, hardMax) => {
        const sent = body[key];
        let v;
        if (sent === undefined)
            v = base[key];
        else if (sent === null)
            v = null;
        else if (!isNum(sent) || sent < 0 || sent > hardMax) {
            return { error: `${key} must be a number from 0 to ${hardMax}, or null for the maximum.` };
        }
        else
            v = !bodyExplicit && sent === 0 ? null : sent;
        if (v !== null && max > 0 && v > max)
            v = max;
        return v;
    };
    const rate = next('rateOverride', maxRate, 100);
    if (rate !== null && typeof rate === 'object')
        return { ok: false, error: 'INVALID_SURCHARGE_OVERRIDE', message: rate.error };
    const flat = next('flatOverride', maxFlat, Number.MAX_SAFE_INTEGER);
    if (flat !== null && typeof flat === 'object')
        return { ok: false, error: 'INVALID_SURCHARGE_OVERRIDE', message: flat.error };
    const value = { ...base, rateOverride: rate, flatOverride: flat, overridesExplicit: true };
    if (typeof body.apply === 'boolean')
        value.apply = body.apply;
    return { ok: true, value };
}
//# sourceMappingURL=surchargeRates.js.map